import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { isOrchestrationFabric } from '@common/Fabric/utils';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { VAppProviderType } from '@common/VerticalApps/types';
import { compose } from '@prophecy/utils/redux';
import { useEffect, useMemo, useState } from 'react';
import { createStore, StoreEnhancer } from 'redux';

import FabricCrudWrapper from '../../../Fabric/common/FabricCrudWrapper';
import useFormContext from '../../../Fabric/hooks/useFormContext';
import { useIsFabricAdmin } from '../../../Fabric/hooks/useIsFabricAdmin';
import useLoadFabricData from '../../../Fabric/hooks/useLoadFabricData';
import { FABRIC_TABS, FabricProviderType } from '../../../Fabric/types';
import { FabricSetupState } from '../constants';
import { WorkspaceFabricStoreContextProvider } from './context';
import { WorkspaceFabricContextValue } from './types';
import { getFabricNameUsingWorkspace } from './utils';

type OperationType = 'setup' | 'management';

const WorkspaceFabricProviderContent = ({
  children,
  fabricId,
  fabricSetupState,
  type,
  workspaceId,
  workspaceName
}: {
  children: React.ReactNode;
  fabricId: string | undefined;
  fabricSetupState: FabricSetupState;
  workspaceId: string;
  workspaceName: string;
  type: OperationType;
}) => {
  const formContexts = useFormContext(fabricId, FabricProviderType.Orchestration, workspaceId);

  const { isLoading: isFabricLoading } = useLoadFabricData(fabricId || '', formContexts);

  const { isAdmin } = useIsFabricAdmin(formContexts);

  const providerFormContext = formContexts[FABRIC_TABS.providers];

  const { watch } = providerFormContext;

  const fabricProvider = watch('providerV2') as VAppProviderType;

  const { fabrics } = useUserWorkspace();
  const prophecyManagedFabric = useMemo(
    () => fabrics.find((fabric) => isOrchestrationFabric(fabric, ORCHESTRATION_PROVIDER.ProphecyManaged)),
    [fabrics]
  );

  useEffect(
    function updateFabricNameAndDescription() {
      if (type !== 'setup' || fabricSetupState === FabricSetupState.Configured || isFabricLoading) {
        return;
      }

      const uniqueFabricName = getFabricNameUsingWorkspace(workspaceName || '', fabricProvider, fabrics);
      // set fabric name and description
      formContexts[FABRIC_TABS.basic].setValue('name', uniqueFabricName);
      formContexts[FABRIC_TABS.basic].setValue('description', `${fabricProvider} Fabric for ${workspaceName}`);
    },
    [fabricSetupState, isFabricLoading, formContexts, fabricProvider, fabrics, type, workspaceName]
  );

  const value = useMemo<WorkspaceFabricContextValue>(
    () =>
      ({
        allowEditFabric: isAdmin,
        formContexts,
        isAdmin,
        isFabricLoading,
        fabricId,
        workspaceId,
        fabricSetupState,
        fabricProvider,
        prophecyManagedFabric
      }) satisfies WorkspaceFabricContextValue,
    [
      isAdmin,
      formContexts,
      isFabricLoading,
      fabricId,
      fabricSetupState,
      fabricProvider,
      workspaceId,
      prophecyManagedFabric
    ]
  );

  return <WorkspaceFabricStoreContextProvider value={value}>{children}</WorkspaceFabricStoreContextProvider>;
};

export const WorkspaceFabricProvider = ({ children, type }: { children: React.ReactNode; type: OperationType }) => {
  const [fabricId, setFabricId] = useState<string | undefined>();
  const [fabricSetupState, setFabricSetupState] = useState<FabricSetupState>(FabricSetupState.UnConfigured);

  const { currentFabric, currentWorkspace } = useUserWorkspace();

  const workspaceId = currentWorkspace?._id;

  useEffect(
    function updateFabricIdAndSetupState() {
      if (!currentFabric) {
        setFabricId(undefined);
        setFabricSetupState(FabricSetupState.UnConfigured);
        return;
      }

      const fabricProvider = currentFabric?.fabricProviderInfo.provider;

      // TODO: Check for all fabrics later once multiple fabrics are supported
      const fabricSetupState =
        fabricProvider !== ORCHESTRATION_PROVIDER.ProphecyManaged
          ? FabricSetupState.Configured
          : FabricSetupState.UnConfigured;

      let fabricId: string | undefined = currentFabric.fabric.id;

      if (type === 'setup' && fabricSetupState === FabricSetupState.UnConfigured) {
        // create fabric flow
        fabricId = undefined;
      }

      setFabricId(fabricId);
      setFabricSetupState(fabricSetupState);
    },
    [currentFabric, setFabricSetupState, setFabricId, type]
  );

  const store = useMemo(() => {
    return createStore(
      (state) => state,
      { currentGraph: { metainfo: { fabricId } } },
      compose('WorkspaceFabricContext') as StoreEnhancer
    );
  }, [fabricId]);

  return (
    <FabricCrudWrapper store={store} key={fabricId}>
      <WorkspaceFabricProviderContent
        fabricId={fabricId}
        fabricSetupState={fabricSetupState}
        workspaceId={workspaceId || ''}
        workspaceName={currentWorkspace?.name || ''}
        type={type}>
        {children}
      </WorkspaceFabricProviderContent>
    </FabricCrudWrapper>
  );
};
